/**
 * @file The enemy brain, extracted from the enemy STORAGE. Everything here is a
 * pure function or a typed-array data structure: no three.js, no DOM, no
 * allocation in the hot path, no `Math.random()`. That is what makes
 * `tests/enemies.test.mjs` able to drive 300 fixed steps of a full FSM in node
 * without a GPU.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE FSM IS A FREE FUNCTION AND NOT A METHOD
 * ─────────────────────────────────────────────────────────────────────────────
 * `EnemyManager` is a struct-of-arrays: there is no per-enemy OBJECT to hang a
 * method on, and creating one per enemy per step is exactly the allocation the
 * SoA layout exists to avoid. So {@link stepFSM} takes scalars in and writes
 * scalars into a caller-owned `out` record that the manager reuses forever.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SEPARATION HASH
 * ─────────────────────────────────────────────────────────────────────────────
 * Without separation, 120 enemies chasing one player converge onto the same
 * point and become one enemy-shaped blob. {@link SpatialHash} is a 3 m bucket
 * grid rebuilt from scratch every fixed step by a counting sort into two
 * `Int32Array`s — no `Map`, no arrays of arrays, no garbage.
 *
 * The grid is TOROIDAL (cell coordinates are masked into a 64x64 table) rather
 * than bounded: enemies live wherever the level put them, and a wrapped bucket
 * only ever produces a FALSE NEIGHBOUR 192 m away, which the distance test in
 * {@link SpatialHash#separationInto} rejects for free. A bounded grid would
 * need an origin, a rebuild on level bounds change, and a clamp per lookup.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO STATE MACHINES, NOT ONE WITH A FLAG
 * ─────────────────────────────────────────────────────────────────────────────
 * {@link stepFSM} is the wave/hero machine: lose your target and you WANDER.
 * {@link stepHomeFSM} is the squad machine: lose your target and you walk back
 * to your formation slot and stand there. They are separate functions because
 * the idle halves are opposites, because `stepFSM` is pinned by the existing
 * test suite, and because a held formation must not draw a wander rng number.
 * Separation, collision and everything else is shared — teams only change WHO
 * you steer at, never how you move.
 *
 * @module engine/actors/ai
 */

const TAU = Math.PI * 2;

/**
 * Enemy lifecycle. Stored in a `Uint8Array`, so the values matter: `DEAD` is
 * last and doubles as "this slot is free".
 * @enum {number}
 */
export const STATE = Object.freeze({
  IDLE: 0,
  WANDER: 1,
  CHASE: 2,
  ATTACK: 3,
  HURT: 4,
  DYING: 5,
  DEAD: 6,
});

/** Debug/HUD labels, indexed by {@link STATE}. */
export const STATE_NAMES = Object.freeze([
  'idle', 'wander', 'chase', 'attack', 'hurt', 'dying', 'dead',
]);

/**
 * Allegiance, stored in `EnemyManager.team` (a `Uint8Array`, so these are the
 * wire values). `HOSTILE` is 0 because that is what a zeroed array already says:
 * every existing `spawn()` call in hero mode and in the three shipped levels
 * lands on team 0 without knowing this enum exists.
 *
 * Units only ever target the OPPOSING team, and only when the manager is run
 * with `ctx.targeting === 'teams'` — hero mode never reads this array.
 * @enum {number}
 */
export const TEAM = Object.freeze({
  HOSTILE: 0,
  SQUAD: 1,
});

/**
 * WHAT KIND OF MIND a slot has, stored in `EnemyManager.brain` (a `Uint8Array`,
 * so these are wire values). `NONE` is 0 because a zeroed array must already
 * say "a normal fighter" — every hero-mode spawn and every existing level lands
 * on it without knowing this enum exists, and the manager's frozen hero path
 * therefore never leaves the fighter branch.
 *
 * The two additions are the CITY's, not the arena's:
 *  • `CIVILIAN` runs {@link stepPedFSM} instead of {@link stepFSM} — it strolls
 *    between waypoints the population director hands it, pauses, and runs when
 *    it is frightened. It never acquires a target and never swings.
 *  • `SEATED` is inert: no steering, no separation, no collision. Its transform
 *    is written from OUTSIDE every step (a traffic car's seat anchor). It is
 *    still shootable, still bleeds and still dies — a passenger you cannot kill
 *    is the one thing a player will always test.
 * @enum {number}
 */
export const BRAIN = Object.freeze({
  NONE: 0,
  CIVILIAN: 1,
  SEATED: 2,
});

/**
 * A pedestrian's MOOD, stored in `EnemyManager.mood`. Orthogonal to
 * {@link STATE}: a civilian keeps using STATE for the things the renderers read
 * (HURT flash, DYING shrink, DEAD = free slot) and uses this for what it is
 * actually doing.
 * @enum {number}
 */
export const PED = Object.freeze({
  STROLL: 0,
  PAUSE: 1,
  FLEE: 2,
});

/** Debug labels, indexed by {@link PED}. */
export const PED_NAMES = Object.freeze(['stroll', 'pause', 'flee']);

/**
 * Pedestrian tuning. Speeds are FRACTIONS of the character's own `speed`, which
 * is the number the def authors: a civilian's authored speed IS its run, and a
 * stroll is a fraction of it. That way "run away" never needs a second stat and
 * a fast character flees fast.
 */
export const PED_TUNING = Object.freeze({
  /** Stroll speed as a fraction of the type's speed (1.9 m/s -> ~1.05). */
  strollScale: 0.55,
  /** Run speed as a fraction of it. 1.0 = the authored number. */
  fleeScale: 1,
  /** Seconds of strolling before an idle stop is considered. */
  strollMin: 5,
  strollMax: 14,
  /** Seconds an idle stop lasts. */
  pauseMin: 1,
  pauseMax: 4,
  /** Seconds after the last scare before a civilian calms down. */
  fleeTime: 6,
  /** How often the flight bearing is re-jittered, seconds. */
  fleeRefresh: 0.5,
  /** Radians of jitter on the away-from-the-scare bearing. */
  fleeJitter: 0.45,
  /** Metres from a waypoint that counts as "arrived". */
  goalReach: 1.2,
  /** A gunshot within this many metres scares a civilian. */
  shotRadius: 14,
  /** A death within this many metres does. */
  deathRadius: 18,
  /** A fast car within this many metres does. */
  carRadius: 7,
  /** ...and "fast" is this, m/s. */
  carSpeed: 6,
});

/**
 * THE tuning table. Every number here was chosen for a 6.5 m/s player on the
 * fixed 60 Hz step; see `dev/scenes/enemies.js` for the live A/B.
 *
 * @typedef {Object} AiTuning
 * @property {number} aggro    Metres. Inside this, an idle/wandering enemy commits.
 * @property {number} deAggro  Metres. Beyond this a chaser gives up (> `aggro`,
 *   or enemies flicker on the boundary).
 * @property {number} reachPad Added to `playerRadius + enemyRadius` for the
 *   attack reach — the gap the claw covers.
 * @property {number} windup   Seconds from entering ATTACK to the contact hit.
 * @property {number} recover  Seconds after the hit before the next swing.
 * @property {number} hurtTime Seconds of knockback stagger.
 * @property {number} dyingTime Seconds of death shrink before the slot frees.
 * @property {number} attackHold Multiplier on `reach`: how far the player may
 *   drift and still eat the follow-up swing (hysteresis).
 * @property {number} wanderSpeed Fraction of the type's speed used to drift.
 * @property {number} separation Strength of the neighbour push, in units of the
 *   type's speed.
 * @property {number} sepClamp Hard cap on the accumulated push, so a 12-enemy
 *   pile-up does not launch anyone.
 * @property {number} knockback Metres/second imparted by a hit.
 * @property {number} leash SQUAD MODE ONLY. Metres a unit with a formation slot
 *   may stray from it before it breaks off and walks back. This is the number
 *   that makes a formation read as a formation instead of a mob: too large and
 *   the line dissolves into the first engagement, too small and units bounce off
 *   an enemy they could have reached.
 * @property {number} homeSnap SQUAD MODE ONLY. Metres. Inside this the unit is
 *   "home": it plants (IDLE) and — unlike an idle wave enemy — never wanders,
 *   which is also why a held unit draws no rng.
 */

/** @type {Readonly<AiTuning>} */
export const AI_DEFAULTS = Object.freeze({
  aggro: 18,
  deAggro: 26,
  reachPad: 0.25,
  windup: 0.4,
  recover: 0.7,
  hurtTime: 0.18,
  dyingTime: 0.22,
  attackHold: 1.25,
  wanderSpeed: 0.34,
  separation: 1.15,
  sepClamp: 1.6,
  knockback: 4.2,
  // Squad mode. Inert in hero mode — nothing reads them without `home` set.
  leash: 11,
  homeSnap: 0.5,
});

/**
 * Merges a partial tuning over {@link AI_DEFAULTS}. Flat, so a plain spread —
 * exported so levels/scenes can describe "fast, twitchy enemies" in one object.
 * @param {Partial<AiTuning>|null} [over]
 * @returns {AiTuning}
 */
export function mergeTuning(over) {
  return over ? Object.freeze({ ...AI_DEFAULTS, ...over }) : AI_DEFAULTS;
}

// ─────────────────────────────────────────────────────────────────────────────
// Steering
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Unit direction from A to B, plus the distance — one `sqrt` for both, because
 * every caller needs the distance anyway (aggro bands, attack reach).
 *
 * @param {number} fromX @param {number} fromZ
 * @param {number} toX @param {number} toZ
 * @param {{x: number, z: number, d: number}} out REUSED — never hold onto it.
 * @returns {{x: number, z: number, d: number}} `out`. `d === 0` leaves the
 *   direction at (0,0) rather than producing NaN.
 */
export function seek(fromX, fromZ, toX, toZ, out) {
  const dx = toX - fromX;
  const dz = toZ - fromZ;
  const d = Math.sqrt(dx * dx + dz * dz);
  out.d = d;
  if (d < 1e-6) {
    out.x = 0;
    out.z = 0;
  } else {
    out.x = dx / d;
    out.z = dz / d;
  }
  return out;
}

/**
 * A fresh wander heading. Deterministic in `(rng, t)`: the rng draw picks the
 * quadrant-ish direction, the time term adds a slow common drift so a crowd of
 * idle enemies mills in loose waves instead of jittering independently — which
 * is what makes a mob read as ALIVE rather than as noise.
 *
 * @param {import('../types.js').Rng} rng
 * @param {number} [t=0] Seconds (the level clock is fine).
 * @returns {number} Heading in radians (unwrapped; callers use sin/cos).
 */
export function wanderDir(rng, t = 0) {
  return rng() * TAU + Math.sin(t * 0.37) * 0.8;
}

// ─────────────────────────────────────────────────────────────────────────────
// FSM
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The record {@link stepFSM} writes into. One per manager, reused forever.
 * @typedef {Object} FsmOut
 * @property {number} state    Next {@link STATE}.
 * @property {number} timer    Seconds in the (possibly new) state.
 * @property {boolean} attack  TRUE on the single step a windup lands. The
 *   manager turns this into one `'player:damage'` emit.
 * @property {boolean} retarget TRUE when the caller should draw a new wander
 *   heading + dwell time.
 */

/** @returns {FsmOut} A zeroed record, for callers that want their own. */
export function newFsmOut() {
  return { state: STATE.IDLE, timer: 0, attack: false, retarget: false };
}

/**
 * ONE enemy, ONE fixed step of the state machine. Pure: same inputs, same
 * outputs, no reads of anything outside its arguments.
 *
 * ```
 *            dist <= aggro                dist <= reach
 *   IDLE ─────────────────► CHASE ◄──────────────────► ATTACK
 *    ▲ ▲                     │  ▲   dist > reach*hold    │
 *    │ └─dwell─► WANDER ─────┘  │                        │
 *    │              ▲           └──────── HURT ◄─────────┘ (damage)
 *    └───dwell──────┘                       │
 *                     DYING ──dyingTime──► DEAD  (hp <= 0, any state)
 * ```
 *
 * The windup fires on the step the timer CROSSES `tune.windup`, so it lands
 * exactly once per swing regardless of the step size — and only if the player is
 * still inside `reach * attackHold`, so backing off mid-swing actually dodges.
 *
 * @param {number} state Current {@link STATE}.
 * @param {number} timer Seconds already spent in it.
 * @param {number} dwell Per-enemy randomised IDLE/WANDER dwell, seconds.
 * @param {number} dist  Distance to the player, metres.
 * @param {number} reach Attack reach for THIS enemy (playerR + enemyR + pad).
 * @param {number} dt    Fixed step, seconds.
 * @param {AiTuning} tune
 * @param {FsmOut} out   REUSED output record.
 * @returns {FsmOut} `out`
 */
export function stepFSM(state, timer, dwell, dist, reach, dt, tune, out) {
  const t = timer + dt;
  out.state = state;
  out.timer = t;
  out.attack = false;
  out.retarget = false;

  switch (state) {
    case STATE.IDLE:
      if (dist <= tune.aggro) {
        out.state = STATE.CHASE;
        out.timer = 0;
      } else if (t >= dwell) {
        out.state = STATE.WANDER;
        out.timer = 0;
        out.retarget = true;
      }
      break;

    case STATE.WANDER:
      if (dist <= tune.aggro) {
        out.state = STATE.CHASE;
        out.timer = 0;
      } else if (t >= dwell) {
        out.state = STATE.IDLE;
        out.timer = 0;
        out.retarget = true;
      }
      break;

    case STATE.CHASE:
      if (dist <= reach) {
        out.state = STATE.ATTACK;
        out.timer = 0;
      } else if (dist > tune.deAggro) {
        out.state = STATE.WANDER;
        out.timer = 0;
        out.retarget = true;
      }
      break;

    case STATE.ATTACK:
      // Crossing test, not `>=`: fires on exactly one step per swing.
      if (timer < tune.windup && t >= tune.windup && dist <= reach * tune.attackHold) {
        out.attack = true;
      }
      if (t >= tune.windup + tune.recover) {
        out.timer = 0;
        if (dist <= reach * tune.attackHold) out.state = STATE.ATTACK;
        else if (dist <= tune.deAggro) out.state = STATE.CHASE;
        else {
          out.state = STATE.WANDER;
          out.retarget = true;
        }
      }
      break;

    case STATE.HURT:
      if (t >= tune.hurtTime) {
        out.timer = 0;
        if (dist <= tune.deAggro) out.state = STATE.CHASE;
        else {
          out.state = STATE.WANDER;
          out.retarget = true;
        }
      }
      break;

    case STATE.DYING:
      if (t >= tune.dyingTime) {
        out.state = STATE.DEAD;
        out.timer = 0;
      }
      break;

    default: // DEAD — inert; the manager frees the slot.
      break;
  }

  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// FSM — the formation-slot variant (squad mode)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * {@link stepFSM} for a unit that OWNS A SLOT. Same shape, same `out` record, and
 * it exists as a second function rather than a flag on the first for two reasons:
 *
 *  1. `stepFSM` is pinned by `tests/enemies.test.mjs` and drives all three
 *     shipped levels — it does not get a new parameter.
 *  2. A slotted unit's idle behaviour is the OPPOSITE of a wave enemy's. A wave
 *     enemy that loses its target wanders; a squad unit that loses its target
 *     walks back to its slot and stands there. This machine therefore NEVER
 *     emits `WANDER` and NEVER sets `retarget` — which is also what keeps a held
 *     formation from drawing a single rng number per step.
 *
 * ```
 *   (not engaged)                       (engaged)
 *   homeDist > homeSnap ─► CHASE(home)   dist <= reach ─► ATTACK ─┐
 *   homeDist <= homeSnap ─► IDLE(hold)   else          ─► CHASE   │
 *                                          ▲                     │
 *                                          └──── HURT ◄──────────┘
 *                             DYING ──dyingTime──► DEAD
 * ```
 *
 * The caller decides `engaged` — that is where the leash, the aggro band and the
 * target's liveness live, because all three need manager state this function is
 * deliberately blind to.
 *
 * A RANGED unit is expressed by passing a negative `reach`: `dist <= reach` can
 * then never hold, so it stays in CHASE and never swings. The manager plants it
 * at `fire.range` and applies the shot itself.
 *
 * @param {number} state Current {@link STATE}.
 * @param {number} timer Seconds already spent in it.
 * @param {number} dist  Metres to the unit's target (`Infinity` when it has none).
 * @param {number} homeDist Metres from its slot.
 * @param {boolean} engaged Caller's verdict: is it fighting right now?
 * @param {number} reach Attack reach (targetR + own R + pad), or < 0 for ranged.
 * @param {number} dt
 * @param {AiTuning} tune
 * @param {FsmOut} out REUSED output record. `out.retarget` is always false.
 * @returns {FsmOut} `out`
 */
export function stepHomeFSM(state, timer, dist, homeDist, engaged, reach, dt, tune, out) {
  const t = timer + dt;
  out.state = state;
  out.timer = t;
  out.attack = false;
  out.retarget = false;

  if (state === STATE.DEAD) return out;

  if (state === STATE.DYING) {
    if (t >= tune.dyingTime) {
      out.state = STATE.DEAD;
      out.timer = 0;
    }
    return out;
  }

  if (state === STATE.HURT) {
    if (t >= tune.hurtTime) {
      out.timer = 0;
      out.state = engaged || homeDist > tune.homeSnap ? STATE.CHASE : STATE.IDLE;
    }
    return out;
  }

  if (!engaged) {
    // Break-off / hold. A unit that was mid-swing when its target died drops the
    // swing rather than finishing it at thin air.
    const want = homeDist > tune.homeSnap ? STATE.CHASE : STATE.IDLE;
    if (want !== state) {
      out.state = want;
      out.timer = 0;
    }
    return out;
  }

  switch (state) {
    case STATE.ATTACK:
      // Same crossing test as `stepFSM`: exactly one contact per swing.
      if (timer < tune.windup && t >= tune.windup && dist <= reach * tune.attackHold) {
        out.attack = true;
      }
      if (t >= tune.windup + tune.recover) {
        out.timer = 0;
        out.state = dist <= reach * tune.attackHold ? STATE.ATTACK : STATE.CHASE;
      }
      break;

    default: // IDLE / WANDER / CHASE — engaged, so close and swing.
      if (dist <= reach) {
        out.state = STATE.ATTACK;
        out.timer = 0;
      } else if (state !== STATE.CHASE) {
        out.state = STATE.CHASE;
        out.timer = 0;
      }
      break;
  }

  return out;
}

/**
 * Should a slotted unit be walking home right now? Pure hysteresis over one
 * boolean, kept here so the manager's step stays a straight line.
 *
 * A unit that merely crosses `leash` once must not flip back the instant it
 * steps inside again, or a squad member parked exactly on the boundary
 * oscillates between "charge" and "retreat" every step. So: break off at
 * `leash`, and stay broken off all the way back to `homeSnap`.
 *
 * @param {boolean} returning Its current flag.
 * @param {number} homeDist Metres from its slot.
 * @param {AiTuning} tune
 * @returns {boolean} The new flag.
 */
export function leashCheck(returning, homeDist, tune) {
  if (homeDist > tune.leash) return true;
  if (returning && homeDist <= tune.homeSnap) return false;
  return returning;
}

// ─────────────────────────────────────────────────────────────────────────────
// FSM — the pedestrian variant (the city's civilians)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The record {@link stepPedFSM} writes into. One per manager, reused forever.
 * @typedef {Object} PedOut
 * @property {number} mood     Next {@link PED}.
 * @property {number} timer    Seconds in the (possibly new) mood.
 * @property {boolean} retarget TRUE when the caller should draw a new dwell —
 *   and, entering STROLL, a fresh waypoint.
 * @property {boolean} calm    TRUE on the single step a flight ends. The
 *   population director uses it to hand the civilian a pavement waypoint again;
 *   until then it is running through whatever is in its way.
 */

/** @returns {PedOut} A zeroed record, for callers that want their own. */
export function newPedOut() {
  return { mood: PED.STROLL, timer: 0, retarget: false, calm: false };
}

/**
 * ONE civilian, ONE fixed step of the MOOD machine. Pure, like {@link stepFSM}.
 *
 * ```
 *   STROLL ──dwell──► PAUSE ──dwell──► STROLL
 *      ▲                                  ▲
 *      └────── FLEE ──fleeTime───────────-┘   (scare() enters FLEE from anywhere)
 * ```
 *
 * A scare does NOT go through this function: it is an external event, so the
 * manager sets the mood directly (and re-sets the timer on every further
 * scare, which is what makes a running crowd keep running while the shooting
 * lasts). This machine only ever runs a mood OUT.
 *
 * @param {number} mood Current {@link PED}.
 * @param {number} timer Seconds already spent in it.
 * @param {number} dwell Randomised STROLL/PAUSE dwell, seconds.
 * @param {number} dt
 * @param {PedOut} out REUSED output record.
 * @returns {PedOut} `out`
 */
export function stepPedFSM(mood, timer, dwell, dt, out) {
  const t = timer + dt;
  out.mood = mood;
  out.timer = t;
  out.retarget = false;
  out.calm = false;

  switch (mood) {
    case PED.PAUSE:
      if (t >= dwell) {
        out.mood = PED.STROLL;
        out.timer = 0;
        out.retarget = true;
      }
      break;

    case PED.FLEE:
      if (t >= PED_TUNING.fleeTime) {
        out.mood = PED.STROLL;
        out.timer = 0;
        out.retarget = true;
        out.calm = true;
      }
      break;

    default: // STROLL
      if (t >= dwell) {
        out.mood = PED.PAUSE;
        out.timer = 0;
        out.retarget = true;
      }
      break;
  }

  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Separation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A rebuilt-every-step uniform bucket grid over the live enemies. Counting sort
 * into two flat `Int32Array`s; zero allocation after construction.
 *
 * Usage per fixed step:
 * ```js
 * hash.build(manager.top, manager.x, manager.z, manager.solid);
 * for (const i of live) hash.separationInto(i, x, z, radius, push);
 * ```
 */
export class SpatialHash {
  /**
   * @param {Object} [o]
   * @param {number} [o.cell=3] Bucket size in metres. Should be >= the largest
   *   `enemyRadius * 2` or neighbours slip between cells.
   * @param {number} [o.cap=512] Max entities (matches the manager's cap).
   * @param {number} [o.dim=64] Table edge; MUST be a power of two. 64 cells of
   *   3 m = 192 m before the torus wraps.
   */
  constructor({ cell = 3, cap = 512, dim = 64 } = {}) {
    if ((dim & (dim - 1)) !== 0) throw new Error('SpatialHash: `dim` must be a power of two');
    /** @type {number} */
    this.cell = cell > 0 ? cell : 3;
    this.invCell = 1 / this.cell;
    /** @type {number} */
    this.dim = dim | 0;
    this.mask = this.dim - 1;
    this.cap = cap | 0;

    const nb = this.dim * this.dim;
    /** CSR row starts, length `nb + 1`. */
    this._starts = new Int32Array(nb + 1);
    /** Write cursors during {@link build}. */
    this._cursor = new Int32Array(nb);
    /** Entity ids, grouped by bucket. */
    this._items = new Int32Array(this.cap);
    /** Bucket per entity, or -1 when it did not participate. */
    this._bucket = new Int32Array(this.cap).fill(-1);
    /** @type {number} Entities inserted by the last {@link build}. */
    this.count = 0;
  }

  /**
   * @param {number} cx @param {number} cz Integer cell coordinates.
   * @returns {number} Bucket index (wrapped into the table).
   */
  bucketOf(cx, cz) {
    return ((cz & this.mask) * this.dim + (cx & this.mask)) | 0;
  }

  /**
   * Rebuilds from a slice of the manager's arrays.
   * @param {number} n Slots to consider (`manager.top`).
   * @param {Float32Array} xs
   * @param {Float32Array} zs
   * @param {Uint8Array} live Non-zero = participates in separation.
   * @returns {this}
   */
  build(n, xs, zs, live) {
    const nb = this.dim * this.dim;
    const starts = this._starts;
    starts.fill(0);

    const lim = Math.min(n, this.cap);
    let total = 0;
    for (let i = 0; i < lim; i++) {
      if (!live[i]) {
        this._bucket[i] = -1;
        continue;
      }
      const b = this.bucketOf(Math.floor(xs[i] * this.invCell), Math.floor(zs[i] * this.invCell));
      this._bucket[i] = b;
      starts[b + 1]++;
      total++;
    }
    for (let b = 0; b < nb; b++) starts[b + 1] += starts[b];
    this._cursor.set(starts.subarray(0, nb));
    for (let i = 0; i < lim; i++) {
      const b = this._bucket[i];
      if (b >= 0) this._items[this._cursor[b]++] = i;
    }
    this.count = total;
    return this;
  }

  /**
   * Accumulates the push that keeps enemy `i` out of its neighbours, as a sum of
   * unit vectors weighted by overlap fraction (0 at touching, 1 at concentric).
   * Linear in overlap, not inverse-square: a hard falloff makes a dense mob
   * explode, and the whole point is a stable jostle.
   *
   * @param {number} i
   * @param {Float32Array} xs
   * @param {Float32Array} zs
   * @param {Float32Array} radii
   * @param {{x: number, z: number}} out REUSED; overwritten.
   * @returns {number} Neighbours that contributed.
   */
  separationInto(i, xs, zs, radii, out) {
    out.x = 0;
    out.z = 0;
    const x = xs[i];
    const z = zs[i];
    const r = radii[i];
    const cx = Math.floor(x * this.invCell);
    const cz = Math.floor(z * this.invCell);
    const starts = this._starts;
    const items = this._items;
    let hits = 0;

    for (let oz = -1; oz <= 1; oz++) {
      for (let ox = -1; ox <= 1; ox++) {
        const b = this.bucketOf(cx + ox, cz + oz);
        const end = starts[b + 1];
        for (let k = starts[b]; k < end; k++) {
          const j = items[k];
          if (j === i) continue;
          const ex = x - xs[j];
          const ez = z - zs[j];
          const rr = r + radii[j];
          const d2 = ex * ex + ez * ez;
          if (d2 >= rr * rr) continue;
          if (d2 < 1e-10) {
            // Perfectly stacked (a double spawn on the same tile). Push apart
            // along a DETERMINISTIC axis derived from the ids, never a random
            // one — two runs of the same seed must not diverge here.
            const s = j < i ? 1 : -1;
            out.x += s * 0.7;
            out.z += s * 0.7;
            hits++;
            continue;
          }
          const d = Math.sqrt(d2);
          const w = (rr - d) / rr;
          out.x += (ex / d) * w;
          out.z += (ez / d) * w;
          hits++;
        }
      }
    }
    return hits;
  }

  /** @returns {{cells: number, count: number, cell: number}} */
  stats() {
    return { cells: this.dim * this.dim, count: this.count, cell: this.cell };
  }
}

export { TAU };
